import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Activity, Check, Trash2, X } from 'lucide-react';
import {
  BORG_CR10_LABELS,
  BORG_CR10_VALUES,
  createEmptyMedicalRecordVitalSignsDraft,
  formatBorgValue,
  formatMedicalRecordVitalSigns,
  hasMedicalRecordVitalSignsDraftValues,
  parseMedicalRecordVitalSignsDraft,
  toLocalDateTimeInput,
  type MedicalRecordVitalSignsDraft,
} from '../../utils/medicalRecordVitalSigns';

interface EvolutionVitalSignsFieldProps {
  value: MedicalRecordVitalSignsDraft;
  onChange: (value: MedicalRecordVitalSignsDraft) => void;
  fallbackMeasuredAt?: string | Date | null;
  disabled?: boolean;
}

interface BorgScaleFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
}

const BorgScaleField: React.FC<BorgScaleFieldProps> = ({ label, value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const numericValue = value.trim() ? Number(value.replace(',', '.')) : null;
  const selectedValue = numericValue != null && Number.isFinite(numericValue) ? numericValue : null;

  return (
    <div>
      <span className="mb-1 block text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
        {label}
      </span>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="flex min-h-16 w-full flex-col items-start justify-between gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-left text-sm text-slate-900 hover:border-blue-300 hover:bg-blue-50/30 sm:flex-row sm:items-center"
      >
        <span className="min-w-0">
          <span className="block font-semibold">
            {selectedValue == null ? 'Abrir escala de Borg' : `Borg ${formatBorgValue(selectedValue)}`}
          </span>
          <span className="mt-0.5 block text-xs leading-5 text-slate-500">
            {selectedValue == null
              ? 'Mostre a escala ao paciente.'
              : BORG_CR10_LABELS[selectedValue] || 'Valor registrado'}
          </span>
        </span>
        <span className="shrink-0 whitespace-nowrap text-xs font-semibold text-blue-700">Ver escala</span>
      </button>
      {selectedValue != null && (
        <button
          type="button"
          onClick={() => onChange('')}
          className="mt-1.5 text-xs font-medium text-slate-500 underline hover:text-slate-800"
        >
          Limpar valor
        </button>
      )}

      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/45 p-3 sm:p-4">
          <div className="max-h-[92vh] w-full max-w-4xl overflow-y-auto rounded-3xl border border-slate-200 bg-white p-4 shadow-2xl sm:p-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-xs font-black uppercase tracking-[0.18em] text-blue-700">
                  Escala de Borg CR10
                </div>
                <h4 className="mt-2 text-xl font-semibold text-slate-900">{label}</h4>
                <p className="mt-1 text-sm text-slate-600">
                  Mostre a escala ao paciente e selecione o valor informado.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                Fechar
              </button>
            </div>

            <div className="mt-5 overflow-x-auto">
              <div className="inline-grid min-w-full grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-6 xl:grid-cols-12">
                {BORG_CR10_VALUES.map((borgValue) => (
                  <button
                    key={borgValue}
                    type="button"
                    onClick={() => {
                      onChange(String(borgValue));
                      setIsOpen(false);
                    }}
                    className={`flex min-w-24 flex-col items-center justify-center gap-1 rounded-2xl border p-3 text-center transition-colors ${
                      selectedValue === borgValue
                        ? 'border-blue-700 bg-blue-700 text-white shadow-sm'
                        : 'border-slate-200 bg-white hover:bg-slate-50'
                    }`}
                  >
                    <span className="text-base font-semibold leading-tight">
                      {formatBorgValue(borgValue)}
                    </span>
                    {BORG_CR10_LABELS[borgValue] && (
                      <span className="whitespace-pre-line text-[11px] leading-tight opacity-80">
                        {BORG_CR10_LABELS[borgValue]}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:justify-end">
              {selectedValue != null && (
                <button
                  type="button"
                  onClick={() => {
                    onChange('');
                    setIsOpen(false);
                  }}
                  className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Limpar valor
                </button>
              )}
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="rounded-xl bg-blue-700 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-800"
              >
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const EvolutionVitalSignsField: React.FC<EvolutionVitalSignsFieldProps> = ({
  value,
  onChange,
  fallbackMeasuredAt,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const parsed = useMemo(
    () => parseMedicalRecordVitalSignsDraft(value, fallbackMeasuredAt),
    [fallbackMeasuredAt, value],
  );
  const summary = parsed.value ? formatMedicalRecordVitalSigns(parsed.value) : null;

  useEffect(() => {
    if (!isOpen) return;

    const closeOnOutsideClick = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('mousedown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [isOpen]);

  const updateField = (field: keyof MedicalRecordVitalSignsDraft, nextValue: string) => {
    setValidationError(null);
    onChange({ ...value, [field]: nextValue });
  };

  const openEditor = () => {
    if (!hasMedicalRecordVitalSignsDraftValues(value)) {
      onChange({
        ...value,
        measuredAtLocal: toLocalDateTimeInput(fallbackMeasuredAt),
      });
    }
    setValidationError(null);
    setIsOpen(true);
  };

  const completeEditor = () => {
    const result = parseMedicalRecordVitalSignsDraft(value, fallbackMeasuredAt);
    if (result.error) {
      setValidationError(result.error);
      return;
    }

    setValidationError(null);
    setIsOpen(false);
  };

  const clearValues = () => {
    onChange(createEmptyMedicalRecordVitalSignsDraft(fallbackMeasuredAt));
    setValidationError(null);
  };

  return (
    <div ref={containerRef} className="relative flex min-w-0 flex-wrap items-center justify-end gap-2">
      {summary && (
        <span className="max-w-full truncate text-xs font-medium text-emerald-700" title={summary}>
          {summary}
        </span>
      )}

      <button
        type="button"
        onClick={() => (isOpen ? setIsOpen(false) : openEditor())}
        disabled={disabled}
        aria-expanded={isOpen}
        className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-700 transition-colors hover:border-blue-300 hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <Activity className="h-3.5 w-3.5" />
        {summary ? 'Editar sinais vitais' : 'Sinais vitais'}
      </button>

      {isOpen && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-slate-950/20 p-3 sm:p-5">
          <div className="max-h-[calc(100vh-1.5rem)] w-full max-w-3xl overflow-y-auto rounded-2xl border border-slate-200 bg-white p-4 shadow-2xl sm:max-h-[calc(100vh-2.5rem)] sm:p-5">
          <div className="mb-4 flex items-start justify-between gap-3">
            <div>
              <h4 className="text-sm font-semibold text-slate-900">Sinais vitais do atendimento</h4>
              <p className="mt-0.5 text-xs text-slate-500">
                Preencha somente os dados aferidos. Campos não informados serão omitidos do prontuário.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
              aria-label="Fechar sinais vitais"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <label className="text-xs font-medium text-slate-600">
              Frequência cardíaca (FC)
              <div className="mt-1 flex items-center gap-1.5">
                <input
                  type="number"
                  min="20"
                  max="300"
                  step="1"
                  inputMode="numeric"
                  value={value.heartRateBpm}
                  onChange={(event) => updateField('heartRateBpm', event.target.value)}
                  className="h-10 min-w-0 w-full rounded-lg border-slate-300 text-sm shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  placeholder="78"
                />
                <span className="shrink-0 text-[11px] text-slate-400">bpm</span>
              </div>
            </label>

            <label className="text-xs font-medium text-slate-600">
              Saturação de oxigênio (SpO₂)
              <div className="mt-1 flex items-center gap-1.5">
                <input
                  type="number"
                  min="1"
                  max="100"
                  step="1"
                  inputMode="numeric"
                  value={value.oxygenSaturationPercent}
                  onChange={(event) => updateField('oxygenSaturationPercent', event.target.value)}
                  className="h-10 min-w-0 w-full rounded-lg border-slate-300 text-sm shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  placeholder="98"
                />
                <span className="shrink-0 text-[11px] text-slate-400">%</span>
              </div>
            </label>

            <label className="text-xs font-medium text-slate-600">
              Frequência respiratória (FR)
              <div className="mt-1 flex items-center gap-1.5">
                <input
                  type="number"
                  min="1"
                  max="100"
                  step="1"
                  inputMode="numeric"
                  value={value.respiratoryRateBpm}
                  onChange={(event) => updateField('respiratoryRateBpm', event.target.value)}
                  className="h-10 min-w-0 w-full rounded-lg border-slate-300 text-sm shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  placeholder="16"
                />
                <span className="shrink-0 text-[11px] text-slate-400">irpm</span>
              </div>
            </label>

            <label className="text-xs font-medium text-slate-600">
              Pressão arterial (PA)
              <div className="mt-1 flex items-center gap-1.5">
                <input
                  type="number"
                  min="40"
                  max="300"
                  step="1"
                  inputMode="numeric"
                  aria-label="Pressão arterial sistólica"
                  value={value.systolicPressureMmHg}
                  onChange={(event) => updateField('systolicPressureMmHg', event.target.value)}
                  className="h-10 min-w-0 w-full rounded-lg border-slate-300 text-sm shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  placeholder="120"
                />
                <span className="text-slate-400">/</span>
                <input
                  type="number"
                  min="20"
                  max="200"
                  step="1"
                  inputMode="numeric"
                  aria-label="Pressão arterial diastólica"
                  value={value.diastolicPressureMmHg}
                  onChange={(event) => updateField('diastolicPressureMmHg', event.target.value)}
                  className="h-10 min-w-0 w-full rounded-lg border-slate-300 text-sm shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  placeholder="80"
                />
                <span className="shrink-0 text-[11px] text-slate-400">mmHg</span>
              </div>
            </label>
          </div>

          <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50/60 p-3">
            <div className="mb-3">
              <h5 className="text-sm font-semibold text-slate-900">Escala de Borg CR10</h5>
              <p className="mt-0.5 text-xs text-slate-500">
                Registre dispneia e fadiga somente quando avaliadas.
              </p>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <section>
                <h6 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-600">Inicial</h6>
                <div className="grid gap-3">
                  <BorgScaleField
                    label="Borg dispneia inicial"
                    value={value.borgDyspneaInitial}
                    onChange={(nextValue) => updateField('borgDyspneaInitial', nextValue)}
                  />
                  <BorgScaleField
                    label="Borg fadiga inicial"
                    value={value.borgFatigueInitial}
                    onChange={(nextValue) => updateField('borgFatigueInitial', nextValue)}
                  />
                </div>
              </section>
              <section>
                <h6 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-600">Final</h6>
                <div className="grid gap-3">
                  <BorgScaleField
                    label="Borg dispneia final"
                    value={value.borgDyspneaFinal}
                    onChange={(nextValue) => updateField('borgDyspneaFinal', nextValue)}
                  />
                  <BorgScaleField
                    label="Borg fadiga final"
                    value={value.borgFatigueFinal}
                    onChange={(nextValue) => updateField('borgFatigueFinal', nextValue)}
                  />
                </div>
              </section>
            </div>
          </div>

          <label className="mt-4 block max-w-xs text-xs font-medium text-slate-600">
            Data e hora da aferição
            <input
              type="datetime-local"
              value={value.measuredAtLocal}
              onChange={(event) => updateField('measuredAtLocal', event.target.value)}
              className="mt-1 h-10 w-full rounded-lg border-slate-300 text-sm shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </label>

          {validationError && (
            <p className="mt-3 text-xs font-medium text-rose-600">{validationError}</p>
          )}

          <div className="mt-4 flex items-center justify-between gap-3 border-t border-slate-100 pt-3">
            <button
              type="button"
              onClick={clearValues}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-rose-600"
            >
              <Trash2 className="h-3.5 w-3.5" /> Limpar
            </button>
            <button
              type="button"
              onClick={completeEditor}
              className="inline-flex items-center gap-1.5 rounded-lg bg-blue-700 px-3 py-2 text-xs font-semibold text-white hover:bg-blue-800"
            >
              <Check className="h-3.5 w-3.5" /> Concluir
            </button>
          </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EvolutionVitalSignsField;
